import { useState } from 'react'
import { Shield, FileJson, Database, AlertTriangle } from 'lucide-react'

export default function AdminStub() {
  const [activeTab, setActiveTab] = useState('overview')

  return (
    <div className="space-y-6 animate-slide-in">
      <div className="flex items-center">
        <Shield className="w-8 h-8 text-primary-600 mr-3" />
        <div>
          <h1 className="text-3xl font-bold">Admin Tools</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Local content management and data preview
          </p>
        </div>
      </div>

      <div className="card bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-yellow-800 dark:text-yellow-200">Development Only</h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              This page is for local development and content management.
              In production, content is managed via JSON files in /public/data.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
        {['overview', 'data', 'manifest'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Content Files</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/oir_questions.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/wat_words.csv</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/tat_images.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/srt_questions.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/interview_questions.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/defence_notes.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-mono">/public/data/media_manifest.json</span>
                <span className="text-green-600">✓ Available</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button className="btn-secondary w-full flex items-center justify-center">
                <FileJson className="w-5 h-5 mr-2" />
                Validate All JSON Files
              </button>
              <button className="btn-secondary w-full flex items-center justify-center">
                <Database className="w-5 h-5 mr-2" />
                Preview All Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Tab */}
      {activeTab === 'data' && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Data Preview</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Use browser DevTools or navigate to individual practice modules to preview data.
          </p>
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <code className="text-sm">
              Data files are loaded dynamically via useFetchData hook.
              <br />
              Check Network tab in DevTools to see data requests.
            </code>
          </div>
        </div>
      )}

      {/* Manifest Tab */}
      {activeTab === 'manifest' && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Media Manifest</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Manage media references (Google Drive URLs, local thumbnails)
          </p>
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg font-mono text-xs overflow-x-auto">
            <pre>{`{
  "images": [
    {
      "id": "tat-001",
      "url": "https://drive.google.com/uc?id=...",
      "thumbnail": "/assets/tat-thumbs/tat-001.jpg"
    }
  ]
}`}</pre>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Edit /public/data/media_manifest.json to update media references.
          </p>
        </div>
      )}
    </div>
  )
}
